import type { CSSProperties, ElementType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import styles from './OverflowContainer.module.css';

interface OverflowContainerProps<T extends ElementType> {
  as?: T;
  children: ReactNode;
  className?: string;
  castHeight?: number;
  containerHeight?: number;
  maxHeight?: number;
}

export const OverflowContainer = <T extends ElementType>({
  as,
  children,
  className,
  castHeight = 12,
  containerHeight,
  maxHeight,
}: OverflowContainerProps<T>) => {
  const Component = as ?? 'div';
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showTopCast, setShowTopCast] = useState(false);
  const [showBottomCast, setShowBottomCast] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    const content = contentRef.current;

    if (element && content) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = element;

        setShowTopCast(scrollTop > 0);
        setShowBottomCast(scrollTop + clientHeight < scrollHeight);
      };

      element.addEventListener('scroll', handleScroll);

      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(element);
      resizeObserver.observe(content);

      handleScroll();

      return () => {
        element.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [containerHeight, maxHeight, children]);

  const sizing = {
    '--overflow-container-cast-height': `${castHeight}px`,
    '--overflow-container-height': containerHeight === undefined ? 'auto' : `${containerHeight}px`,
    '--overflow-container-max-height': maxHeight === undefined ? 'none' : `${maxHeight}px`,
  } as CSSProperties;

  return (
    <div className={clsx(styles.container, className)} style={sizing}>
      {/* Top Cast */}
      {showTopCast && <div className={clsx(styles.cast, styles.top)} />}
      <Component className={styles.scroll} ref={scrollRef}>
        <div ref={contentRef}>{children}</div>
      </Component>
      {/* Bottom Cast */}
      {showBottomCast && <div className={clsx(styles.cast, styles.bottom)} />}
    </div>
  );
};
